// Mirrors the QuestionType enum in the Prisma schema (packages/database). Defined
// here as a plain constant so shared stays free of @prisma/client — importing the
// generated client would otherwise pull Prisma into the browser bundle. Keep the
// values in sync with the Prisma enum.
export const QuestionType = {
  BEHAVIOURAL: "BEHAVIOURAL",
  SITUATIONAL: "SITUATIONAL",
} as const;
export type QuestionType = (typeof QuestionType)[keyof typeof QuestionType];

/** Display label for each question type — one definition, used by the API and the web app alike. */
export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  [QuestionType.BEHAVIOURAL]: "Behavioural",
  [QuestionType.SITUATIONAL]: "Situational",
};

/**
 * What a "different question" retry of one item can currently land on, given
 * everything the candidate has already been asked for that competency and
 * type. Lets the retake picker warn before the sitting is created rather
 * than only after (substitutionDeadEnds).
 */
export const SubstitutionAvailability = {
  /** A question the candidate has never been asked in this group. */
  Unseen: "UNSEEN",
  /** Nothing unseen left — the pick rotates back to a question they've answered before. */
  RepeatOnly: "REPEAT_ONLY",
  /** No other question in the group at all — the pick keeps the current question. */
  None: "NONE",
} as const;
export type SubstitutionAvailability =
  (typeof SubstitutionAvailability)[keyof typeof SubstitutionAvailability];

// Mirrors the AssessmentInputMode enum in the Prisma schema (packages/database),
// same rationale as QuestionType above.
export const AssessmentInputMode = {
  TEXT: "TEXT",
  VOICE: "VOICE",
} as const;
export type AssessmentInputMode = (typeof AssessmentInputMode)[keyof typeof AssessmentInputMode];

// Mirrors the HtsInputType enum in the Prisma schema (packages/database), same
// rationale as QuestionType above.
export const HtsInputType = {
  TEXT: "TEXT",
  TEXTAREA: "TEXTAREA",
  DATE: "DATE",
  SELECT: "SELECT",
  CHECKBOXES: "CHECKBOXES",
  YES_NO: "YES_NO",
  // Candidate renders an add/remove-row form per HtsQuestion.groupFields
  // (employment history, education entries, volunteer roles, etc). The
  // backend never parses row structure — the candidate UI compiles all rows
  // into one JSON-stringified answer and submits it like any other value.
  REPEATABLE_GROUP: "REPEATABLE_GROUP",
} as const;
export type HtsInputType = (typeof HtsInputType)[keyof typeof HtsInputType];

// Canonical answer values stored for an HtsQuestion of inputType YES_NO.
export const HtsYesNoAnswer = {
  YES: "yes",
  NO: "no",
} as const;
export type HtsYesNoAnswer = (typeof HtsYesNoAnswer)[keyof typeof HtsYesNoAnswer];

// Mirrors the HtsSubmissionStatus enum in the Prisma schema (packages/database),
// same rationale as HtsInputType above.
export const HtsSubmissionStatus = {
  DRAFT: "DRAFT",
  SUBMITTED: "SUBMITTED",
} as const;
export type HtsSubmissionStatus = (typeof HtsSubmissionStatus)[keyof typeof HtsSubmissionStatus];

// The candidate-facing projection of HtsSubmissionStatus above, used by the
// session-history list and the Readiness summary endpoint. Deliberately its
// own vocabulary: "ready" means submitted and viewable (scored or not), which
// isn't the same statement as the DB's SUBMITTED.
export const HtsSubmissionSummaryStatus = {
  Ready: "ready",
  Draft: "draft",
  // A mandatory review trigger fired and no admin has released this
  // submission's profile yet — neither score nor narrative is shown.
  PendingReview: "pending_review",
  // An access-code guest's submission: never shown to the candidate, released
  // or not — their results go to the requesting agency.
  WithheldFromCandidate: "withheld_from_candidate",
} as const;
export type HtsSubmissionSummaryStatus =
  (typeof HtsSubmissionSummaryStatus)[keyof typeof HtsSubmissionSummaryStatus];

// Where a guest's admin follow-up round stands. Derived from the submission's timestamps
// (deriveHtsAdminFollowUpStatus), never stored.
export const HtsAdminFollowUpStatus = {
  NotSent: "not_sent",
  AwaitingCandidate: "awaiting_candidate",
  // Closed: the guest answered, or the admin confirmed none were needed.
  Answered: "answered",
} as const;
export type HtsAdminFollowUpStatus = (typeof HtsAdminFollowUpStatus)[keyof typeof HtsAdminFollowUpStatus];

// Mirrors the HtsAnswerClassification enum in the Prisma schema
// (packages/database), same rationale as HtsInputType above. Assigned by the
// htsSuitabilityClassifier agent (EVT-78) to any answer whose question has a
// non-null classificationTopic.
export const HtsAnswerClassification = {
  SUITABLE: "SUITABLE",
  MARGINALLY_SUITABLE: "MARGINALLY_SUITABLE",
  MARGINALLY_UNSUITABLE: "MARGINALLY_UNSUITABLE",
  UNSUITABLE: "UNSUITABLE",
} as const;
export type HtsAnswerClassification = (typeof HtsAnswerClassification)[keyof typeof HtsAnswerClassification];

export const HTS_ANSWER_CLASSIFICATION_LABELS: Record<HtsAnswerClassification, string> = {
  SUITABLE: "Suitable",
  MARGINALLY_SUITABLE: "Marginally suitable",
  MARGINALLY_UNSUITABLE: "Marginally unsuitable",
  UNSUITABLE: "Unsuitable",
};

// Mirrors the HtsFlagPriority enum in the Prisma schema (packages/database).
// Always derived deterministically in code from HtsAnswerClassification
// (never trusted from the classifier agent directly) — see
// htsSuitabilityFollowUp.ts.
export const HtsFlagPriority = {
  STANDARD: "STANDARD",
  HIGH: "HIGH",
} as const;
export type HtsFlagPriority = (typeof HtsFlagPriority)[keyof typeof HtsFlagPriority];

// Mirrors the HtsConsistencyFlagOverrideDecision enum in the Prisma schema
// (packages/database). An admin's yes/no call on whether an
// HtsConsistencyFlag's contradiction actually holds — not CONFIRMED/EXCLUDED,
// since the only thing at stake is whether one specific penalty (Honesty &
// Completeness's unresolved-flag cap) applies.
export const HtsConsistencyFlagOverrideDecision = {
  INVALID: "INVALID",
  VALID: "VALID",
} as const;
export type HtsConsistencyFlagOverrideDecision =
  (typeof HtsConsistencyFlagOverrideDecision)[keyof typeof HtsConsistencyFlagOverrideDecision];

// ── Admin review override/note discriminators ────────────────────────────────
// Every admin override is appended to an encrypted history blob whose entries
// are a discriminated union. Defined here so the API schemas, request
// validation, and the admin review page all build from one place rather than
// repeating string literals. The original automated value is never one of
// these — it stays in its own column.

/** Discriminator for an `HtsAnswer.encryptedOverrideHistory` entry. */
export const HtsAnswerOverrideHistoryKind = {
  Classification: "classification",
  HardGate: "hardGate",
  Note: "note",
} as const;
export type HtsAnswerOverrideHistoryKind =
  (typeof HtsAnswerOverrideHistoryKind)[keyof typeof HtsAnswerOverrideHistoryKind];

/** Discriminator for an `HtsConsistencyFlag.encryptedOverrideHistory` entry. */
export const HtsConsistencyFlagOverrideHistoryKind = {
  Decision: "decision",
  Note: "note",
} as const;
export type HtsConsistencyFlagOverrideHistoryKind =
  (typeof HtsConsistencyFlagOverrideHistoryKind)[keyof typeof HtsConsistencyFlagOverrideHistoryKind];

/** Discriminator for a Readiness dimension's `overrideHistory` entry (inside `HtsReadinessProfile.encryptedDetail`). */
export const HtsReadinessDimensionOverrideHistoryKind = {
  Score: "score",
  Note: "note",
} as const;
export type HtsReadinessDimensionOverrideHistoryKind =
  (typeof HtsReadinessDimensionOverrideHistoryKind)[keyof typeof HtsReadinessDimensionOverrideHistoryKind];

/** Which kind of row a standalone note in an admin review batch attaches to. */
export const HtsReviewNoteTarget = {
  Answer: "answer",
  ConsistencyFlag: "consistencyFlag",
  Dimension: "dimension",
  /** The perfect-person probe, which has no HtsAnswer row of its own. */
  Probe: "probe",
} as const;
export type HtsReviewNoteTarget = (typeof HtsReviewNoteTarget)[keyof typeof HtsReviewNoteTarget];

/** Discriminator for a `HtsReadinessNarrativeHistoryEntry` — who authored that version of the narrative. */
export const HtsReadinessNarrativeSource = {
  /** Drafted by the model, triggered by an admin's dimension-score change. */
  Model: "model",
  /** Hand-edited by an admin, whether starting from a model draft or the existing narrative. */
  AdminEdit: "adminEdit",
} as const;
export type HtsReadinessNarrativeSource =
  (typeof HtsReadinessNarrativeSource)[keyof typeof HtsReadinessNarrativeSource];

// Mirrors the HtsClassificationTopic enum in the Prisma schema
// (packages/database). Which Section B topic group a classifiable question
// belongs to — see
// packages/shared/src/htsClassificationTopics/*.ts for each topic's
// follow-up/flag config, rendered into the htsSuitabilityClassifier agent's
// prompt.
export const HtsClassificationTopic = {
  // ── SUITABILITY kind — Section B, drives htsSuitabilityClassifier ─────────
  CRIMINAL_CHARGES_CONVICTIONS: "CRIMINAL_CHARGES_CONVICTIONS",
  INTEGRITY_HONESTY: "INTEGRITY_HONESTY",
  WORKPLACE_TRUST_CONDUCT: "WORKPLACE_TRUST_CONDUCT",
  PERSONAL_CONDUCT_HISTORY: "PERSONAL_CONDUCT_HISTORY",
  ONLINE_SOCIAL_MEDIA_CONDUCT: "ONLINE_SOCIAL_MEDIA_CONDUCT",
  FINANCIAL_LEGAL_COMPLETENESS: "FINANCIAL_LEGAL_COMPLETENESS",
  REGULATORY_CLEARANCE_HISTORY: "REGULATORY_CLEARANCE_HISTORY",
  DRIVING_RECORD_BEHAVIOUR: "DRIVING_RECORD_BEHAVIOUR",
  SUBSTANCE_USE: "SUBSTANCE_USE",
  // Fallback for a future admin-authored question that needs the classify/
  // follow-up engine but doesn't cleanly match any topic above.
  GENERAL: "GENERAL",

  // ── READINESS kind — Sections D/E, drives htsReadinessFollowUp ────────────
  // A topic means "probe this answer when it's vague". It says nothing about
  // scoring — which pillar an answer feeds is the question's own
  // `readinessPillars`, so an informational Section A/C question can feed a
  // pillar without a topic, and Section E has a topic while feeding nothing.
  FITNESS: "FITNESS",
  JOB_FIT: "JOB_FIT",
  MOTIVATION: "MOTIVATION",
  LEADERSHIP: "LEADERSHIP",
  SPECIAL_SKILLS: "SPECIAL_SKILLS",
  SELF_AWARENESS: "SELF_AWARENESS",
} as const;
export type HtsClassificationTopic = (typeof HtsClassificationTopic)[keyof typeof HtsClassificationTopic];

// Which engine a topic's answers run through — SUITABILITY drives
// htsSuitabilityClassifier/htsSuitabilityFollowUp.ts, READINESS drives
// htsReadinessFollowUp.ts + htsReadinessScoring.ts. Never both. See each
// topic's `kind` in packages/shared/src/htsClassificationTopics/*.ts.
export const HtsTopicKind = {
  Suitability: "SUITABILITY",
  Readiness: "READINESS",
} as const;
export type HtsTopicKind = (typeof HtsTopicKind)[keyof typeof HtsTopicKind];

// Mirrors the HtsReadinessBand enum in the Prisma schema (packages/database).
// Purely derived from compositeScore via HTS_READINESS_BAND_THRESHOLDS below
// — never set independently. See Evaltick Readiness Scoring Rubric.
export const HtsReadinessBand = {
  FOUNDATIONAL: "FOUNDATIONAL",
  DEVELOPING: "DEVELOPING",
  COMPETITIVE: "COMPETITIVE",
  HIGHLY_COMPETITIVE: "HIGHLY_COMPETITIVE",
} as const;
export type HtsReadinessBand = (typeof HtsReadinessBand)[keyof typeof HtsReadinessBand];

export const HTS_READINESS_BAND_THRESHOLDS: ReadonlyArray<{
  min: number;
  max: number;
  band: HtsReadinessBand;
  label: string;
}> = [
  { min: 0, max: 39, band: HtsReadinessBand.FOUNDATIONAL, label: "Foundational" },
  { min: 40, max: 59, band: HtsReadinessBand.DEVELOPING, label: "Developing" },
  { min: 60, max: 79, band: HtsReadinessBand.COMPETITIVE, label: "Competitive" },
  { min: 80, max: 100, band: HtsReadinessBand.HIGHLY_COMPETITIVE, label: "Highly Competitive" },
];

/** Reports the composite's band per HTS_READINESS_BAND_THRESHOLDS; throws if compositeScore is outside 0–100. */
export const getHtsReadinessBand = (compositeScore: number): HtsReadinessBand => {
  const match = HTS_READINESS_BAND_THRESHOLDS.find((t) => compositeScore >= t.min && compositeScore <= t.max);
  if (!match) throw new Error(`compositeScore ${compositeScore} is outside the 0–100 band range`);
  return match.band;
};

// The 10 Readiness Score pillars (Evaltick Readiness Scoring Rubric) — not
// flat 10% each; see compositeWeight (schemas.ts) and computeCompositeScore.
// A questionnaire missing every question for a pillar's topic(s) omits that
// pillar rather than scoring it as 0 (see htsReadinessScoring.ts). Integrity
// is the one pillar not driven by a dedicated READINESS topic — see
// packages/shared/src/htsReadinessPillars/integrity.ts.
export const HtsReadinessPillarKey = {
  LIFE_SKILLS: "LIFE_SKILLS",
  WORK_EXPERIENCE: "WORK_EXPERIENCE",
  EDUCATION: "EDUCATION",
  VOLUNTEERING: "VOLUNTEERING",
  INTEGRITY: "INTEGRITY",
  FITNESS: "FITNESS",
  JOB_FIT: "JOB_FIT",
  MOTIVATION: "MOTIVATION",
  LEADERSHIP: "LEADERSHIP",
  SPECIAL_SKILLS: "SPECIAL_SKILLS",
} as const;
export type HtsReadinessPillarKey = (typeof HtsReadinessPillarKey)[keyof typeof HtsReadinessPillarKey];

// Version of each HTS "Disclosure & Consent" variant in HtsWaiverModal —
// guests and signed-in candidates see different notices. Recorded onto each
// HtsSubmission at creation (consentVersion) so the audit trail captures
// exactly which wording was agreed to. Bump a variant when its notice changes;
// bump both when the shared checkbox label does.
export const HTS_WAIVER_VERSION = {
  Guest: "v2-guest",
  Candidate: "v2-candidate",
} as const;

// The fixed "perfect person" probe — a constant, not admin-authored or
// LLM-generated. Injected at most once per submission by
// maybeAskPerfectPersonProbe when every classificationTopic-tagged answer
// comes back SUITABLE and unflagged.
export const HTS_PERFECT_PERSON_PROBE_QUESTION =
  "Have you ever done something — even something minor — that you knew at the time was wrong, dishonest, or against a rule?";

// Synthetic id for rendering the probe through the same components as a real
// question. It has no InterviewTemplateHtsQuestion row of its own, so it can
// never collide with a real cuid.
export const HTS_PERFECT_PERSON_PROBE_ID = "perfect-person-probe";

// How many answered follow-up rounds a single HTS question may accumulate
// before the engine stops asking. Enforced in code, never by the model —
// runHtsSuitabilityFollowUpEngine forbids a further *question* at the cap
// (htsSuitabilityFollowUp.ts) while still classifying the final answer.
//
// Lives here rather than beside that engine so anything re-deriving the gate
// — the eval contract scorers included — reads the same number instead of
// copying it and drifting, same reasoning as isSelfNegatingConsistencyFlag.
export const HTS_MAX_FOLLOW_UP_ROUNDS = 3;

export const ScoreValue = {
  Unsuitable: 1,
  Developing: 2,
  Competent: 3,
  Strong: 4,
  Exceptional: 5,
} as const;
export type ScoreValue = (typeof ScoreValue)[keyof typeof ScoreValue];

export const ScoreLabel: Record<ScoreValue, string> = {
  [ScoreValue.Unsuitable]: "Unsuitable",
  [ScoreValue.Developing]: "Developing",
  [ScoreValue.Competent]: "Competent",
  [ScoreValue.Strong]: "Strong",
  [ScoreValue.Exceptional]: "Exceptional",
};

export const CompetencySlug = {
  DecisionMaking: "decision-making",
  Teamwork: "teamwork",
  Integrity: "integrity",
  InteractiveCommunication: "interactive-communication",
  ProblemSolving: "problem-solving",
  ConflictManagement: "conflict-management",
  AssertivenessSelfConfidence: "assertiveness-self-confidence",
  Adaptability: "adaptability",
  OrganizationalAwareness: "organizational-awareness",
  RiskManagement: "risk-management",
  StressManagement: "stress-management",
  Compassion: "compassion",
  Leadership: "leadership",
  Excellence: "excellence",
  CriticalStrategicThinking: "critical-strategic-thinking",
  PlanningOrganization: "planning-organization",
  ValuingDiversity: "valuing-diversity",
  InterpersonalSkills: "interpersonal-skills",
  Resiliency: "resiliency",
  MotivationResultsOriented: "motivation-results-oriented",
  ContinuousLearningImprovement: "continuous-learning-improvement",
  AnalyticalThinking: "analytical-thinking",
  DealingWithDifficultSituations: "dealing-with-difficult-situations",
  CommunityServiceOrientation: "community-service-orientation",
  SelfInitiativePerseverance: "self-initiative-perseverance",
  Decisiveness: "decisiveness",
  SelfAwareness: "self-awareness",
  AcceptanceOfResponsibility: "acceptance-of-responsibility",
  TimeManagement: "time-management",
  WorkEthic: "work-ethic",
  SelfControlComposure: "self-control-composure",
  AttentionToDetail: "attention-to-detail",
  Professionalism: "professionalism",
  ValuesEthics: "values-ethics",
  Judgement: "judgement",
  CourageOfConvictions: "courage-of-convictions",
  RelationshipBuilding: "relationship-building",
  OralCommunication: "oral-communication",
  Inclusivity: "inclusivity",
  StressTolerance: "stress-tolerance",
  CommunityInvolvementAwareness: "community-involvement-awareness",
  DevelopingOthers: "developing-others",
  StrategicThinking: "strategic-thinking",
  BehaviouralFlexibility: "behavioural-flexibility",
  PublicAccountability: "public-accountability",
  CommunityRelationsMediaManagement: "community-relations-media-management",
  HrTalentManagement: "hr-talent-management",
  ChangeManagement: "change-management",
  FinancialManagement: "financial-management",
  FactFindingObservationalSkills: "fact-finding-observational-skills",
} as const;
export type CompetencySlug = (typeof CompetencySlug)[keyof typeof CompetencySlug];

// The structure score (how well an answer is delivered, judged separately from
// competency content) is graded 0–MAX_STRUCTURE_SCORE. Shared by the structure
// rubric, the agent output schema, and the UI so they stay in sync.
export const MAX_STRUCTURE_SCORE = 20;

// The most a structure component scores when its length is well off target
// (see classifyAnswerLengths). A cap, not a deduction: content that already
// scores at or below it is unaffected.
export const OFF_TARGET_LENGTH_SCORE_CAP = 15;

export const MAX_DIMENSION_SCORE = 100;

// How many attempts a comparison surface spans — per-question rationales,
// voice feedback, the session summary, the HTS readiness narrative, and the
// results-page score-trend chart. Counts the current attempt, so a full window
// is this attempt plus MAX_PRIOR_ATTEMPT_HISTORY earlier ones. Shared so those
// windows can't drift apart: the detailed comparison on every one of them only
// ever uses the single immediately-previous attempt, and this bounds the
// supporting score-trend fact shown alongside it.
export const MAX_ATTEMPT_HISTORY = 10;

// The same window expressed as "how many earlier attempts to fetch" — what
// every history query actually takes, since the current attempt is already in
// hand (or, at evaluate time, is the one being scored). Derived rather than
// written out so the two can't drift.
export const MAX_PRIOR_ATTEMPT_HISTORY = MAX_ATTEMPT_HISTORY - 1;

// Assumed speaking pace for estimateSpeakingMs (frameworks.ts) — STARR/CARL
// target times are spoken-interview norms, but answers here are typed.
export const SPEAKING_WORDS_PER_MINUTE = 130;

// Cost/DoS backstop on candidate-authored free text fed to an agent (coach
// message, HTS disclosure/follow-up answer) — sized with a large safety
// margin above any genuine answer (STARR's longer target is 6-7 minutes;
// even a full hour of continuous speech at SPEAKING_WORDS_PER_MINUTE above
// is comfortably under this), not tuned tight against it. Not meant to ever
// be hit by a real candidate.
export const MAX_ANSWER_MESSAGE_LENGTH = 50_000;

export const CoachPhase = {
  Evaluate: "evaluate",
  Clarify: "clarify",
} as const;
export type CoachPhase = (typeof CoachPhase)[keyof typeof CoachPhase];

// What an agent call's `phase` trace metadata can say — the admin traces
// view's Phase column. interviewCoach's two phases reach it through
// StreamingService.traceMetadata; the stateless session-summary call sets its
// own, since it isn't part of a coaching conversation.
export const TracePhase = {
  ...CoachPhase,
  SessionSummary: "session-summary",
} as const;
export type TracePhase = (typeof TracePhase)[keyof typeof TracePhase];

// Which of htsReadinessScorer's two tasks one generate() call is for — picks
// between its two dynamic instruction sets (see htsReadinessScorer.ts), same
// mechanism CoachPhase uses to split interviewCoach's Evaluate/Clarify.
export const HtsReadinessTask = {
  Scoring: "scoring",
  Narrative: "narrative",
} as const;
export type HtsReadinessTask = (typeof HtsReadinessTask)[keyof typeof HtsReadinessTask];

// Dimensions at or above this weight are never eligible for Not assessed,
// regardless of competency size. Surveyed against all 70 competency×
// question-type configs: worst case lost weight is 9% (teamwork/BEHAVIOURAL).
export const NOT_ASSESSED_WEIGHT_CEILING = 0.1;

export const ReqCtxKey = {
  QuestionType: "questionType",
  CompetencyId: "competencyId",
  Phase: "phase",
  PreviousAttempt: "previousAttempt",
  AnswerLengthContext: "answerLengthContext",
  QuestionText: "questionText",
  HtsClassificationTopic: "htsClassificationTopic",
  HtsReadinessTask: "htsReadinessTask",
} as const;
export type ReqCtxKey = (typeof ReqCtxKey)[keyof typeof ReqCtxKey];

export const StreamEventType = {
  Chunk: "chunk",
  Complete: "complete",
  Error: "error",
} as const;
export type StreamEventType = (typeof StreamEventType)[keyof typeof StreamEventType];

// Who spoke a turn in a question's unified conversation (voice + text, across
// however many mode switches) — the vocabulary ConversationEntry, the
// /conversations endpoints, and questionConversation.ts's Mastra message
// roles all share. Named candidate/coach rather than the raw Mastra
// user/assistant roles, matching what the UI actually shows.
export const ConversationRole = {
  Candidate: "candidate",
  Coach: "coach",
} as const;
export type ConversationRole = (typeof ConversationRole)[keyof typeof ConversationRole];

// Which phase a voice session opens in. Shared because it crosses the wire as
// VoiceSessionResponse.initialPhase, and because two of the three values also
// name a turn-detection profile carried inside the server-authored
// session.update control frames the client echoes back (azureRealtimeSession.ts).
//
// Asking is the odd one out: it names an opening, not a profile. The coach
// reads the question aloud from a server-authored response.create, under
// Answering's profile unchanged — see VoiceTurnProfile below.
export const VoiceSessionPhase = {
  Asking: "asking",
  Answering: "answering",
  Discussing: "discussing",
} as const;
export type VoiceSessionPhase = (typeof VoiceSessionPhase)[keyof typeof VoiceSessionPhase];

// The subset of VoiceSessionPhase that actually names a turn-detection
// profile. buildAudioInput takes this rather than the full phase, so Asking
// can't be passed somewhere that would have to silently decide what profile it
// means — it has none of its own.
export type VoiceTurnProfile =
  | typeof VoiceSessionPhase.Answering
  | typeof VoiceSessionPhase.Discussing;

// The two frame types the server authors for the client to echo verbatim over
// the data channel — see RealtimeControlFrameSchema.
export const RealtimeControlFrameType = {
  SessionUpdate: "session.update",
  ResponseCreate: "response.create",
} as const;
export type RealtimeControlFrameType =
  (typeof RealtimeControlFrameType)[keyof typeof RealtimeControlFrameType];

// Azure Realtime's own vocabulary for the fixed parts of a session — declared
// once so the wire schema (RealtimeControlFrameSchema) and the builder that
// actually emits the frames (buildAudioInput/buildControls in
// azureRealtimeSession.ts) can't drift. Why these specific values, and the
// history behind VadEagerness, is documented at buildAudioInput.
export const RealtimeSessionProfile = {
  SessionType: "realtime",
  TurnDetectionType: "semantic_vad",
  VadEagerness: "low",
  NoiseReduction: "far_field",
} as const;

// Cap on the <conversation_so_far> block a fresh/reconnecting voice session
// injects into its instructions (see buildConversationSoFarBlock) — resent on
// every reconnect, and the Realtime API has a real input-token ceiling.
export const MAX_INJECTED_CONVERSATION_TURNS = 30;
export const MAX_INJECTED_CONVERSATION_CHARS = 8_000;

// What the voice client joins consecutive candidate turns with when building
// the submitted answer (useRealtimeVoice.ts), and the same contract the backend
// walks to map a character offset in that answer back to the turn — and so the
// timestamp — it came from (withComputedWordCounts in assessmentResponse.ts).
// Shared because changing it on one side alone silently misaligns every
// per-section duration rather than failing loudly.
export const VOICE_TURN_SEPARATOR = "\n\n";

// Upper bound on candidate turns accepted in one answer's timing payload —
// well above any real answer's turn count.
export const MAX_VOICE_TURNS = 200;

// Backstop against a runaway or abusive live voice call, not a target session
// length — generous on purpose so it never interrupts a genuine candidate.
// Enforced server-side by hanging the Azure call up (voiceSessionLifecycle.ts)
// and mirrored client-side as an earlier, graceful wind-down. Realtime API cost
// scales with audio duration, so this doubles as a spend cap.
//
// Must stay comfortably under Azure's own 60-minute Realtime session ceiling.
// Set to 60 the two race, and Azure wins — its clock starts at session
// creation, ours only once negotiation returns — so it expires the call first,
// the client's teardown beacon closes the span as `natural`, and `length_cap`
// is never recorded for a session that did in fact hit the cap.
export const MAX_VOICE_SESSION_DURATION_MS = 55 * 60 * 1000;

// Which interaction produced a trace, stamped into span metadata by
// voiceSessionLifecycle.ts and read back by the admin traces UI. No Text member
// on purpose: nothing writes one, and absent metadata already means an ordinary
// text-mode agent run.
export const TraceMode = {
  Voice: "voice",
} as const;
export type TraceMode = (typeof TraceMode)[keyof typeof TraceMode];

// Why a tracked voice call ended, stamped into the closed span's metadata by
// voiceSessionLifecycle.ts — distinguishes a session the candidate finished
// from one the length cap cut off. Shared rather than API-local because the
// admin trace UI branches on it to label the trace.
export const VoiceSessionEndReason = {
  Natural: "natural",
  LengthCap: "length_cap",
} as const;
export type VoiceSessionEndReason =
  (typeof VoiceSessionEndReason)[keyof typeof VoiceSessionEndReason];

// What the browser reports about a live voice call's health, via
// POST /voice/session/event — log-only, so a call that dies client-side leaves
// a trace keyed by callId.
export const VoiceClientEvent = {
  PeerState: "peer_state",
  IceState: "ice_state",
  DataChannelClosed: "data_channel_closed",
  DataChannelOpenTimeout: "data_channel_open_timeout",
  RealtimeError: "realtime_error",
  TranscriptionFailed: "transcription_failed",
  MicOff: "mic_off",
  AutoReconnect: "auto_reconnect",
  ReconnectFailed: "reconnect_failed",
} as const;
export type VoiceClientEvent = (typeof VoiceClientEvent)[keyof typeof VoiceClientEvent];
